import { FC, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { useCarDriversApi } from "../../http/carDrivers/api";
import { AdminCarDriver } from "../../http/carDrivers/types";
import { useAdminCarsApi } from "../../http/cars/api";
import { CarCurrentAssignment, CarNoCarDepency, getTypesOfUse, TypeOfUse } from "../../http/cars/types";
import { useUsersApi } from "../../http/users/api";
import { User } from "../../http/users/types";
import { formatDateNumeric, getWarningBoxTitle } from "../../types/utils";
import DatePicker from "../../ui/DatePicker";
import { InfoBox, WarningBox } from "../ui/Boxes";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import reactSelectStyles from "../ui/ReactSelectUtils";
import Switch from "../ui/Switch";

const CAR_FIELDS = ["id", "vehicleIdentificationNumber", "currentPlateNumber", "lastPlateNumber"] as const;
type CarField = (typeof CAR_FIELDS)[number];
type CarSearch = Pick<CarNoCarDepency, CarField>;

const USER_FIELDS = ["id", "fullName", "email", "areNotificationsDisabled"] as const;
type UserField = (typeof USER_FIELDS)[number];
type UserSearch = Pick<User, UserField>;

export interface CarDriverFormBody {
  id: string;
  car: {
    id: string;
    vehicleIdentificationNumber: string;
    currentPlateNumber?: string;
    lastPlateNumber?: string;
  };
  driver: UserSearch;
  startDate: string;
  endDate?: string;
  typeOfUse: TypeOfUse;
  prevEndDate?: string;
  sendMileageEmail?: boolean;
}

type CarDriverFormProps = {
  carDriver?: AdminCarDriver | null;
  onSubmit: (form: CarDriverFormBody, ass?: CarCurrentAssignment) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
  carId?: string;
};

const CarDriverForm: FC<CarDriverFormProps> = ({
  carDriver,
  onSubmit,
  isDisabled,
  isLoading,
  carId,
}: CarDriverFormProps) => {
  const i18n = useI18n();
  const carsApi = useAdminCarsApi();
  const usersApi = useUsersApi();
  const carDriversApi = useCarDriversApi();
  const aborter = useRef(new AbortController());

  const [defaultUserOptions, setDefaultUserOptions] = useState<UserSearch[]>([]);
  const [defaultCarOptions, setDefaultCarOptions] = useState<CarSearch[]>([]);
  const [currentAssignment, setCurrentAssigment] = useState<CarCurrentAssignment>();
  const [selectedCarId, setSelectedCarId] = useState<string | undefined>(carId);

  const typesOfuse = getTypesOfUse(i18n.t);

  // only in creation: tells if the car already had a driver, the mileage email is not sent to the first driver of a car
  const { data: previousDrivers } = carDriversApi.useAdminCarDrivers(
    { fields: ["id"], limit: 1 },
    aborter.current.signal,
    !carDriver && selectedCarId ? selectedCarId : false,
  );

  const loadCurrentAssigmment = async (carId: string) => {
    const currAss = await carsApi.getCurrentAssignment(carId, aborter.current.signal);
    setCurrentAssigment({
      carDriver: currAss.carDriver?.id !== carDriver?.id ? currAss.carDriver : undefined,
      otherCarAssignment: currAss.otherCarAssignment,
    });
  };

  const defaultValues = {
    car: carDriver?.car,
    prevEndDate: currentAssignment?.carDriver?.endDate ?? currentAssignment?.otherCarAssignment?.endDate,
    startDate: carDriver?.startDate,
    endDate: carDriver?.endDate,
    driver: carDriver?.driver,
    typeOfUse: typesOfuse.find((x) => x.value === carDriver?.typeOfUse),
  };

  useEffect(() => {
    const id = carId || carDriver?.car.id;
    !isLoading && id && loadCurrentAssigmment(id);
  }, [carId, carDriver?.car.id, isLoading]);

  return (
    <Form<CarDriverFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => (formData) => onSubmit(formData, currentAssignment)}
      itemCreationInfo={carDriver}
    >
      {({ control, reset, watch, setValue }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [carDriver]);

        useEffect(() => {
          if (currentAssignment) {
            const prevEndDate = currentAssignment?.carDriver?.endDate ?? currentAssignment?.otherCarAssignment?.endDate;

            if (prevEndDate) {
              setValue("prevEndDate", prevEndDate, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }
          }
        }, [currentAssignment, setValue]);

        const prevStartDate =
          currentAssignment?.carDriver?.startDate ?? currentAssignment?.otherCarAssignment?.startDate;
        const startDate = watch("startDate");

        // if startDate is before the already assigned driver/carPool startDate, the endDate is required
        const endDateRequired =
          (currentAssignment?.carDriver && startDate < currentAssignment.carDriver?.startDate) ||
          (currentAssignment?.otherCarAssignment && startDate < currentAssignment.otherCarAssignment?.startDate);

        // if there is an already assigned driver/carPool and the endDate is not required, the toDate is required
        const toDateRequired =
          !!(currentAssignment?.carDriver || currentAssignment?.otherCarAssignment) && !endDateRequired;

        // the same rules are checked by the server before sending the mileage email
        const driver = watch("driver");
        let mileageEmailBlockedReason: string | undefined;
        if (previousDrivers?.total === 0) {
          mileageEmailBlockedReason = i18n.t.carDrivers.mileageEmailFirstAssignment;
        } else if (driver?.areNotificationsDisabled) {
          mileageEmailBlockedReason = i18n.t.carDrivers.mileageEmailNotificationsDisabled(
            i18n.t.common.disableNotifications,
          );
        } else if (driver && !driver.email) {
          mileageEmailBlockedReason = i18n.t.carDrivers.mileageEmailNoEmail;
        }

        useEffect(() => {
          if (mileageEmailBlockedReason) {
            setValue("sendMileageEmail", false);
          }
        }, [mileageEmailBlockedReason, setValue]);

        return (
          <FormFieldsContainer>
            {!carId && (
              <>
                <Controller
                  name="car"
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.t.cars.singular} error={fieldState.error}>
                      {({ labelId, isInvalid }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions<CarSearch, CarField>(
                          aborter,
                          searchTerm,
                          carsApi.getCars,
                          { fields: CAR_FIELDS },
                        );

                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultCarOptions);
                        }, []);

                        return (
                          <AsyncSelect
                            {...field}
                            inputId={labelId}
                            isDisabled={isDisabled || isLoading || !!carDriver}
                            isLoading={isLoading}
                            defaultOptions={defaultCarOptions}
                            loadOptions={loadOptions}
                            placeholder={`${i18n.t.common.select}...`}
                            getOptionLabel={(x) =>
                              x.currentPlateNumber || x.lastPlateNumber || x.vehicleIdentificationNumber
                            }
                            getOptionValue={(x) => x.id}
                            styles={reactSelectStyles<{
                              id: string;
                              vehicleIdentificationNumber: string;
                              currentPlateNumber?: string;
                              lastPlateNumber?: string;
                            }>(isInvalid)}
                            onChange={(v) => {
                              v ? loadCurrentAssigmment(v?.id) : setCurrentAssigment(undefined);
                              setSelectedCarId(v?.id);
                              field.onChange(v);
                            }}
                            menuPosition="fixed"
                          />
                        );
                      }}
                    </FormField>
                  )}
                />
              </>
            )}
            {!!currentAssignment?.otherCarAssignment && (
              <>
                <WarningBox text={getWarningBoxTitle(i18n.t, currentAssignment.otherCarAssignment.type)} />
                <FormField label={i18n.t.common.from}>
                  <Input
                    disabled
                    value={formatDateNumeric(currentAssignment?.otherCarAssignment.startDate, i18n.language) ?? ""}
                  />
                </FormField>
                <Controller
                  name="prevEndDate"
                  control={control}
                  rules={{
                    required: !!toDateRequired,
                    validate: (value) => {
                      if (value && currentAssignment?.otherCarAssignment) {
                        if (value === currentAssignment.otherCarAssignment.startDate) {
                          return i18n.t.common.endDateCantMatchStartDate;
                        }
                        if (value < currentAssignment.otherCarAssignment.startDate) {
                          return i18n.t.common.endDateBeforeStartDate;
                        }
                      }
                      return undefined;
                    },
                  }}
                  render={({ fieldState }) => (
                    <FormField label={i18n.t.common.to} error={fieldState.error} isOptional={!toDateRequired}>
                      {({ isInvalid }) => (
                        <DatePicker
                          onChange={(v) => setValue("prevEndDate", v!)}
                          isInvalid={isInvalid}
                          value={watch("prevEndDate")}
                          disabled={isDisabled || isLoading}
                          clearable={!toDateRequired}
                        />
                      )}
                    </FormField>
                  )}
                />
              </>
            )}
            {!!currentAssignment?.carDriver && (
              <>
                <WarningBox text={i18n.t.common.currentlyAssignToDriver} />
                <FormField label={i18n.t.common.currentDriver}>
                  <Input disabled value={currentAssignment?.carDriver?.driver.fullName} />
                </FormField>
                <FormField label={i18n.t.common.from}>
                  <Input
                    disabled
                    value={formatDateNumeric(currentAssignment?.carDriver.startDate, i18n.language) ?? ""}
                  />
                </FormField>
                <Controller
                  name="prevEndDate"
                  control={control}
                  rules={{
                    required: !!toDateRequired,
                    validate: (value) => {
                      if (value && currentAssignment?.otherCarAssignment) {
                        if (value === currentAssignment.otherCarAssignment.startDate) {
                          return i18n.t.common.endDateCantMatchStartDate;
                        }
                        if (value < currentAssignment.otherCarAssignment.startDate) {
                          return i18n.t.common.endDateBeforeStartDate;
                        }
                      }
                      return undefined;
                    },
                  }}
                  render={({ fieldState }) => (
                    <FormField label={i18n.t.common.to} error={fieldState.error} isOptional={!toDateRequired}>
                      {({ isInvalid }) => (
                        <DatePicker
                          onChange={(v) => setValue("prevEndDate", v!)}
                          isInvalid={isInvalid}
                          value={watch("prevEndDate")}
                          disabled={isDisabled || isLoading}
                          clearable={!toDateRequired}
                        />
                      )}
                    </FormField>
                  )}
                />
              </>
            )}
            <Controller
              name="driver"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.drivers.singular} error={fieldState.error}>
                  {({ labelId, isInvalid }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());

                    const loadOptions = createLoadOptions<UserSearch, UserField>(
                      aborter,
                      searchTerm,
                      usersApi.getAdminUsers,
                      {
                        fields: USER_FIELDS,
                      },
                    );

                    // Fetch initial data on mount
                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultUserOptions);
                    }, []);

                    return (
                      <AsyncSelect
                        menuPosition="fixed"
                        {...field}
                        inputId={labelId}
                        isDisabled={isDisabled || isLoading}
                        isLoading={isLoading}
                        defaultOptions={defaultUserOptions}
                        loadOptions={loadOptions}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.fullName}
                        getOptionValue={(x) => x.id}
                        styles={reactSelectStyles<UserSearch>(isInvalid)}
                      />
                    );
                  }}
                </FormField>
              )}
            />
            <Controller
              name="startDate"
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  if (value) {
                    const prevEndDate = watch("prevEndDate");
                    if (prevStartDate) {
                      if (prevEndDate) {
                        if (value >= prevStartDate && value < prevEndDate) {
                          return i18n.t.common.startDateFallsInCurrentAssignmentPeriod;
                        }
                        // } else {
                        //   if (value >= prevStartDate) {
                        //     return i18n.t.common.startDateMustBeBeforeCurrentAssignmentStartDate;
                        //   }
                      }
                    }
                  }
                  return undefined;
                },
              }}
              render={({ fieldState }) => (
                <FormField label={i18n.t.common.startDate} error={fieldState.error}>
                  {({ isInvalid }) => (
                    <DatePicker
                      onChange={(v) => setValue("startDate", v!)}
                      isInvalid={isInvalid}
                      value={watch("startDate")}
                      disabled={isDisabled || isLoading}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="endDate"
              control={control}
              rules={{
                required: !!endDateRequired,
                validate: (value) => {
                  if (value) {
                    if (value === startDate) {
                      return i18n.t.common.endDateCantMatchStartDate;
                    }
                    if (value < startDate) {
                      return i18n.t.common.endDateBeforeStartDate;
                    }

                    const prevEndDate = watch("prevEndDate");
                    if (!prevEndDate && prevStartDate && value > prevStartDate) {
                      return i18n.t.common.endDateAfterCurrentAssignmentStartDate;
                    }
                    if (prevEndDate && prevStartDate && value > prevStartDate && value < prevEndDate) {
                      return i18n.t.common.endDateFallsInCurrentAssignmentPeriod;
                    }
                  }
                  return undefined;
                },
              }}
              render={({ fieldState }) => (
                <FormField label={i18n.t.common.endDate} error={fieldState.error} isOptional={!endDateRequired}>
                  {({ isInvalid }) => (
                    <DatePicker
                      onChange={(v) => setValue("endDate", v!)}
                      isInvalid={isInvalid}
                      value={watch("endDate")}
                      disabled={isDisabled || isLoading}
                      clearable={!endDateRequired}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="typeOfUse"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.typeOfUse} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Select
                      menuPosition="fixed"
                      {...field}
                      inputId={labelId}
                      options={typesOfuse}
                      isDisabled={isDisabled || isLoading}
                      placeholder={`${i18n.t.common.select}...`}
                      getOptionLabel={(x) => x.label}
                      getOptionValue={(x) => x.value}
                      styles={reactSelectStyles<TypeOfUse>(isInvalid)}
                    />
                  )}
                </FormField>
              )}
            />
            {!carDriver && (
              <FormField label={i18n.t.carDrivers.sendMileageEmail} isOptional>
                {({ labelId }) => (
                  <>
                    <Switch
                      id={labelId}
                      checked={!!watch("sendMileageEmail")}
                      onChange={(checked) => setValue("sendMileageEmail", checked)}
                      disabled={isDisabled || isLoading || !!mileageEmailBlockedReason}
                    />
                    {!!mileageEmailBlockedReason && <InfoBox text={mileageEmailBlockedReason} />}
                  </>
                )}
              </FormField>
            )}
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default CarDriverForm;
